import { Button, Flex } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";

import { useInviteUser } from "../../api/hooks/user/useInviteUser";
import { useUpdateUser } from "../../api/hooks/user/useUpdateUser.ts";
import { useUsers } from "../../api/hooks/user/useUsers";
import SvgPlus from "../../components/Icons/Plus";
import Layout from "../../components/Layout/Layout";
import Table from "../../components/Table/Table";
import { SortDirection } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title.tsx";
import {
  InviteUserInput,
  OrderBy,
  RolePermission,
  UpdateUserInput,
  UserOrderBy,
} from "../../graphql/generated";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { getApiOrderBy } from "../../utils/api";
import { getPagesCount } from "../../utils/pagination.ts";
import { hasPermission } from "../../utils/user.ts";
import { useUsersTableData } from "./hooks/useUsersTableData";
import InviteUserModal from "./InviteUserModal/InviteUserModal";
import { useStyles } from "./UserAccessSetting.styles.ts";

export default function UserAccessSettingPage() {
  const { classes } = useStyles();

  const { user } = useCurrentUserContext();

  const canAdd = hasPermission(user, RolePermission.CanAddUser);

  const canEdit = hasPermission(user, RolePermission.CanEditUser);

  const [
    inviteModalOpened,
    { open: inviteModalOpen, close: inviteModalClose },
  ] = useDisclosure(false);

  const [orderBy, setOrderBy] = useState<UserOrderBy>({
    createdAt: OrderBy.Asc,
  });
  const [page, setPage] = useState<number>(1);
  const [{ data, fetching }, refreshUsers] = useUsers({
    orderBy,
    page,
  });

  const [, inviteUser] = useMutationNotificationWrapper(useInviteUser(), {
    success: { message: "User has been added to the list." },
  });
  const [, updateUser] = useMutationNotificationWrapper(useUpdateUser(), {
    success: { message: "User has been updated." },
  });

  const handleUpdateUser = useCallback(
    (id: number, input: UpdateUserInput) => {
      updateUser({ id, input }).then((data) => {
        if (!data.error) {
          refreshUsers();
        }
      });
    },
    [refreshUsers, updateUser]
  );

  const [columns, rows] = useUsersTableData({
    data: data?.users.data,
    canEdit,
    onChange: handleUpdateUser,
  });

  const pageCount = getPagesCount(data?.users.total);

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getApiOrderBy(sort));
    },
    [setOrderBy]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleInviteUser = useCallback(
    (input: InviteUserInput) => {
      inviteUser({ input }).then((data) => {
        if (!data.error) {
          refreshUsers();
        }
      });
    },
    [inviteUser, refreshUsers]
  );

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 24, sm: 20 }}>
        <Flex gap={20} justify="space-between">
          <Flex gap={20}>
            <Title size="h1">User Access Settings</Title>
          </Flex>
        </Flex>
        {canAdd && (
          <Button
            uppercase
            variant="filledGolden"
            leftIcon={<SvgPlus />}
            className={classes.inviteModalButton}
            onClick={inviteModalOpen}
          >
            Add
          </Button>
        )}
        <Table
          loading={fetching}
          columns={columns}
          rows={rows}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          defaultSort={{
            columnAccessor: "createdAt",
            direction: SortDirection.asc,
          }}
          onSortChange={handleSortChange}
        />
        <InviteUserModal
          opened={inviteModalOpened}
          onSubmit={handleInviteUser}
          onClose={inviteModalClose}
        />
      </Flex>
    </Layout>
  );
}
